import React, { useEffect, useState } from "react";
import { MapPin, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useForm, ValidationError } from "@formspree/react";

const FadeUp = ({ children, delay = 0, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: delay / 1000, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const serviceOptions = [
  "Security Solutions (Guarding)",
  "Facilities Management",
  "Aviation & Event Services",
  "Cash Management Services",
  "Manpower & Workforce Solutions",
  "Recruitment Inquiry",
  "Other Directives",
];

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [state, handleSubmit] = useForm("mdayangv");

  useEffect(() => {
    if (state.succeeded) {
      setForm(initialForm);
    }
  }, [state.succeeded]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleWhatsApp = (data) => {
    const message = `Hi ATF Team, I have an inquiry.\n\nName: ${data.full_name}\nPhone: ${data.phone}\nEmail: ${data.email}\nService: ${data.service}\nMessage: ${data.message}`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/917567137300?text=${encodedMessage}`, "_blank");
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(e);
    handleWhatsApp(form);
  };

  const inputClass =
    "w-full font-sans text-sm font-medium tracking-wide text-[#141414] bg-white border border-[#141414]/10 rounded-xl px-6 h-14 focus:outline-none focus:ring-2 focus:ring-[#835F21] focus:border-transparent transition-all placeholder:text-[#141414]/30 shadow-sm hover:border-[#141414]/20";

  return (
    <main className="bg-[#F9F8F5] font-sans text-[#141414]" data-testid="contact-page">
      <section className="bg-[#F9F8F5] h-[85vh] md:h-[75vh] flex items-center relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-y-0 left-0 w-full lg:w-[40%] bg-[#F9F8F5]/90 backdrop-blur-sm z-10 pointer-events-none"
            style={{ clipPath: "polygon(0 0, 85% 0, 100% 100%, 0 100%)" }}
          />
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/indian_security_home.png"
              alt="Corporate Communications"
              className="w-full h-full object-cover object-[80%_center] opacity-100"
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F9F8F5] to-transparent pointer-events-none" />
        </div>

        <div className="relative z-20 w-full lg:w-[40%] px-6 md:px-12 lg:pl-24 lg:pr-12 py-12 md:py-16">
          <div className="relative z-10">
            <span className="inline-block px-4 py-1.5 bg-[#835F21]/10 text-[#835F21] text-sm font-bold tracking-widest uppercase mb-6 rounded-full">
              Operations Center
            </span>
            <h1
              className="font-display font-medium text-[#141414] leading-[1.05] mb-8 tracking-tight"
              style={{ fontSize: "clamp(20px, 3vw, 44px)" }}
            >
              Global Network <br />
              <span className="text-[#835F21] italic font-light mt-2 block">Immediate Response.</span>
            </h1>
            <p className="font-sans text-lg md:text-xl text-[#141414]/70 leading-relaxed font-light">
              Our operations team is available 24/7 to manage deployments and emergency logistics across all strategic sectors.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F9F8F5] py-20 md:py-32" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-32">
            <FadeUp delay={100}>
              <div className="bg-[#141414] rounded-[2.5rem] p-10 md:p-12 text-white relative overflow-hidden shadow-2xl border border-white/5">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#835F21]/15 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />

                <div className="relative z-10 space-y-12">
                  <div>
                    <span className="font-sans text-[10px] font-bold tracking-[0.3em] text-[#835F21] uppercase block mb-6">Support Hub</span>
                    <div className="space-y-8">
                      <div className="group cursor-pointer">
                        <p className="font-sans text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] mb-2">Office Helpline</p>
                        <p className="font-display text-3xl font-medium tracking-tight text-white group-hover:text-[#835F21] transition-colors mb-2">
                          +91 90813 48900
                        </p>
                        <div className="space-y-1">
                          <p className="font-sans text-xs text-white/50">+91 98089 83333</p>
                          <p className="font-sans text-xs text-white/50">+91 98982 74903</p>
                        </div>
                      </div>
                      <div className="group cursor-pointer">
                        <p className="font-sans text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] mb-2">Email Addresses</p>
                        <div className="flex flex-col gap-2">
                          <a
                            href="mailto:Secure@atfsecure.com"
                            className="font-sans text-lg text-white/80 hover:text-[#835F21] transition-colors border-b border-white/10 pb-1 w-fit"
                          >
                            secure@atfsecure.com
                          </a>
                          <a
                            href="mailto:Astrumholding@gmail.com"
                            className="font-sans text-lg text-white/80 hover:text-[#835F21] transition-colors border-b border-white/10 pb-1 w-fit"
                          >
                            astrumholding@gmail.com
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-10 border-t border-white/5">
                    <span className="font-sans text-[10px] font-bold tracking-[0.3em] text-[#835F21] uppercase block mb-6">Headquarters</span>
                    <div className="flex items-start gap-5">
                      <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                        <MapPin size={18} className="text-[#835F21]" />
                      </div>
                      <div>
                        <p className="font-sans text-[16px] text-white font-medium mb-1">Gandhinagar, Gujarat</p>
                        <p className="font-sans text-[14px] text-white/40 leading-relaxed font-light">
                          Office #410, Naxtra By Kavyaratna, <br />
                          Sargasan Cross Road, Below The Fern Residency, <br />
                          Gandhinagar, Gujarat - 382421
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-10 border-t border-white/5">
                    <div className="flex items-center justify-between mb-8">
                      <span className="font-sans text-[10px] font-bold tracking-[0.3em] text-[#835F21] uppercase">Deployment Readiness</span>
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        <span className="font-sans text-[9px] font-bold text-green-500 uppercase tracking-widest">Active</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                        <p className="font-sans text-[9px] font-bold text-white/20 uppercase tracking-widest mb-1">Availability</p>
                        <p className="font-sans text-[11px] font-bold text-white uppercase tracking-widest">24 / 7 / 365</p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                        <p className="font-sans text-[9px] font-bold text-white/20 uppercase tracking-widest mb-1">Office Hours</p>
                        <p className="font-sans text-[11px] font-bold text-[#835F21] uppercase tracking-widest">09:00 - 19:00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7 xl:col-span-8">
            <FadeUp delay={200}>
              <div className="bg-white rounded-[2.5rem] p-10 md:p-14 shadow-[0_30px_60px_rgba(0,0,0,0.02)] border border-[#141414]/5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#835F21]/5 blur-3xl -translate-y-1/2 translate-x-1/3 rounded-full" />

                <h2
                  className="font-display font-medium text-[#141414] mb-4 tracking-tight relative z-10"
                  style={{ fontSize: "clamp(32px, 4vw, 42px)" }}
                >
                  Submit Inquiry
                </h2>
                <p className="font-sans text-[15px] font-light text-[#141414]/50 mb-12 relative z-10">
                  Provide your operational requirements below. Once submitted, the inquiry will be sent through your Formspree workflow.
                </p>

                {state.succeeded && (
                  <div className="flex items-center gap-4 bg-[#141414] text-white rounded-2xl px-6 py-5 mb-10 shadow-xl" data-testid="form-success-msg">
                    <CheckCircle size={20} className="text-[#835F21] flex-shrink-0" />
                    <p className="font-sans text-xs font-bold uppercase tracking-[0.15em]">
                      Message Sent Successfully. Awaiting Command Review.
                    </p>
                  </div>
                )}

                {state.errors?.length > 0 && (
                  <div className="flex items-center gap-4 bg-red-500 text-white rounded-2xl px-6 py-5 mb-10 shadow-xl" data-testid="form-error-msg">
                    <AlertCircle size={20} className="flex-shrink-0" />
                    <p className="font-sans text-[11px] font-bold uppercase tracking-[0.15em]">
                      Submission failed. Please review the highlighted fields and try again.
                    </p>
                  </div>
                )}

                <form onSubmit={handleCustomSubmit} data-testid="contact-form" className="relative z-10">
                  <input type="hidden" name="_subject" value={`New Inquiry - ${form.service || "General Contact"}`} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8 mb-8">
                    <div>
                      <label className="font-sans text-[11px] font-bold tracking-[0.15em] text-[#141414]/50 uppercase block mb-3 pl-1" htmlFor="full_name">
                        Full Name *
                      </label>
                      <input
                        id="full_name"
                        name="full_name"
                        type="text"
                        required
                        value={form.full_name}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter your name"
                        data-testid="input-full-name"
                      />
                      <ValidationError prefix="Full Name" field="full_name" errors={state.errors} className="mt-2 text-sm text-red-600" />
                    </div>

                    <div>
                      <label className="font-sans text-[11px] font-bold tracking-[0.15em] text-[#141414]/50 uppercase block mb-3 pl-1" htmlFor="email">
                        Email Address *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="yourname@domain.com"
                        data-testid="input-email"
                      />
                      <ValidationError prefix="Email" field="email" errors={state.errors} className="mt-2 text-sm text-red-600" />
                    </div>

                    <div>
                      <label className="font-sans text-[11px] font-bold tracking-[0.15em] text-[#141414]/50 uppercase block mb-3 pl-1" htmlFor="phone">
                        Phone Number *
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="+91 XXXX XXXXXX"
                        data-testid="input-phone"
                      />
                      <ValidationError prefix="Phone" field="phone" errors={state.errors} className="mt-2 text-sm text-red-600" />
                    </div>

                    <div>
                      <label className="font-sans text-[11px] font-bold tracking-[0.15em] text-[#141414]/50 uppercase block mb-3 pl-1" htmlFor="service">
                        Required Service *
                      </label>
                      <div className="relative">
                        <select
                          id="service"
                          name="service"
                          required
                          value={form.service}
                          onChange={handleChange}
                          className={`${inputClass} appearance-none pr-10`}
                          data-testid="select-service"
                        >
                          <option value="">Awaiting Selection...</option>
                          {serviceOptions.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-[#141414]/30 text-[10px]">
                          ▼
                        </div>
                      </div>
                      <ValidationError prefix="Service" field="service" errors={state.errors} className="mt-2 text-sm text-red-600" />
                    </div>
                  </div>

                  <div className="mb-10">
                    <label className="font-sans text-[11px] font-bold tracking-[0.15em] text-[#141414]/50 uppercase block mb-3 pl-1" htmlFor="message">
                      Message / Requirements *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      value={form.message}
                      onChange={handleChange}
                      className="w-full font-sans text-sm font-medium tracking-wide text-[#141414] bg-white border border-[#141414]/10 rounded-xl px-6 py-6 h-36 focus:outline-none focus:ring-2 focus:ring-[#835F21] focus:border-transparent transition-all resize-none placeholder:text-[#141414]/30 shadow-sm hover:border-[#141414]/20"
                      placeholder="Outline operational requirements..."
                      data-testid="textarea-message"
                    />
                    <ValidationError prefix="Message" field="message" errors={state.errors} className="mt-2 text-sm text-red-600" />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary w-full h-15 text-xs group bg-[#141414] shadow-[0_10px_30px_rgba(0,0,0,0.15)] hover:bg-[#835F21] hover:shadow-[0_15px_40px_rgba(131,95,33,0.3)] tracking-[0.2em]"
                    disabled={state.submitting}
                    data-testid="contact-submit-button"
                  >
                    {state.submitting ? "TRANSMITTING..." : "SUBMIT REQUEST"}
                    <ArrowRight size={18} className="ml-3 group-hover:translate-x-2 transition-transform" />
                  </button>
                </form>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
